<div
    class="widget"
    cdkDrag
    appBaseWidget
    #bw="baseWidget"
    [fillRemaining]="fillRemaining()"
    [fixedSize]="fixedSize()"
    [widgetId]="widgetId()"
    [defaultFraction]="defaultFraction()"
    [class.collapsed]="bw.isCollapsed()"
>
    <div class="widget-header" cdkDragHandle (dblclick)="bw.onHeaderDblClick()">
        <div class="widget-header__left">
            <span class="widget-title">{{ widgetTitle() }}</span>
            @if (warnings() != undefined) {
                <span
                    class="widget-badge"
                    [class.widget-badge--warning]="warnings()!.length > 0"
                    [class.widget-badge--pulse]="badgePulse()"
                >
                    <i class="fa fa-exclamation-triangle"></i>
                    {{ warnings()!.length }}
                </span>
            }
        </div>
        <div class="widget-actions">
            <div
                class="sort-anchor"
                appWidgetDropdown
                #sortDropdown="widgetDropdown"
            >
                <button
                    class="widget-action-btn"
                    [class.active]="manualSort()"
                    [title]="'Sort order' | translate"
                    (click)="sortDropdown.toggle()"
                >
                    <i class="fas fa-sort-amount-down"></i>
                </button>
                @if (sortDropdown.isOpen()) {
                    <section class="sort-popup" aria-label="Warning sort order">
                        <div class="sort-popup__header">
                            <span>{{ 'Sort order' | translate }}</span>
                            <div class="sort-popup__header-actions">
                                <button
                                    class="sort-popup__reset"
                                    [disabled]="!sortActive()"
                                    (click)="resetSort()"
                                    [title]="'Reset sort' | translate"
                                >
                                    <i class="fas fa-rotate-left"></i>
                                </button>
                                <button
                                    class="sort-popup__close"
                                    (click)="sortDropdown.close()"
                                    [title]="'Close' | translate"
                                    aria-label="Close sort panel"
                                >
                                    <i class="fas fa-xmark"></i>
                                </button>
                            </div>
                        </div>
                        <label class="sort-popup__toggle-row">
                            <span>{{ 'Manual' | translate }}</span>
                            <span class="toggle-switch">
                                <input
                                    type="checkbox"
                                    [checked]="manualSort()"
                                    (change)="toggleManualSort()"
                                    aria-label="Enable manual sort order"
                                />
                                <span class="toggle-track"></span>
                            </span>
                        </label>
                        @if (manualSort()) {
                            <p class="sort-popup__drag-hint">
                                {{ 'Drag to reorder' | translate }}
                            </p>
                            <div
                                cdkDropList
                                [cdkDropListData]="customPriority()"
                                class="sort-type-list"
                                (cdkDropListDropped)="onTypeDrop($event)"
                            >
                                @for (type of customPriority(); track type) {
                                    <div class="sort-type-item" cdkDrag>
                                        <i
                                            class="fas fa-grip-vertical sort-type-grip"
                                            aria-hidden="true"
                                        ></i>
                                        <app-warning-icon
                                            [messageType]="type"
                                        ></app-warning-icon>
                                        <span class="sort-type-label">{{
                                            warningHelper.getShortHeader(type)
                                        }}</span>
                                        <ng-template cdkDragPlaceholder>
                                            <div
                                                class="sort-type-placeholder"
                                            ></div>
                                        </ng-template>
                                    </div>
                                }
                            </div>
                        }
                    </section>
                }
            </div>
        </div>
    </div>

    <ng-template cdkDragPlaceholder>
        <div
            class="widget widget-placeholder"
            [style.height.px]="bw.defaultHeightPx()"
        >
            <i class="fa-solid fa-plus" aria-hidden="true"></i>
        </div>
    </ng-template>

    @if (!bw.isCollapsed()) {
        <div class="widget-content">
            @if (warnings() == undefined) {
                <places-spinner [small]="true"></places-spinner>
            } @else if (warnings()!.length === 0) {
                <span class="w-empty">{{ 'No warnings' | translate }}</span>
            } @else {
                <div class="widget-list">
                    <div class="widget-list-header w-header">
                        <button
                            [title]="'Inventory number' | translate"
                            class="sortable"
                            [class.sort-disabled]="manualSort()"
                            (click)="setSort('inv')"
                        >
                            <i class="fa-solid fa-bus-simple"></i>
                            @if (sortCol() === 'inv' && !manualSort()) {
                                <i
                                    [class]="
                                        'sort-icon fas fa-long-arrow-alt-up' +
                                        (sortDir() === 'desc'
                                            ? ' fa-flip-vertical'
                                            : '')
                                    "
                                ></i>
                            }
                        </button>
                        <button
                            [title]="'Route' | translate"
                            class="sortable"
                            [class.sort-disabled]="manualSort()"
                            (click)="setSort('route')"
                        >
                            <i class="fas fa-route"></i>
                            @if (sortCol() === 'route' && !manualSort()) {
                                <i
                                    [class]="
                                        'sort-icon fas fa-long-arrow-alt-up' +
                                        (sortDir() === 'desc'
                                            ? ' fa-flip-vertical'
                                            : '')
                                    "
                                ></i>
                            }
                        </button>
                        <button
                            [title]="'Warning' | translate"
                            class="sortable"
                            [class.sort-disabled]="manualSort()"
                            (click)="setSort('type')"
                        >
                            <i class="fa fa-exclamation-triangle"></i>
                            @if (sortCol() === 'type' && !manualSort()) {
                                <i
                                    [class]="
                                        'sort-icon fas fa-long-arrow-alt-up' +
                                        (sortDir() === 'desc'
                                            ? ' fa-flip-vertical'
                                            : '')
                                    "
                                ></i>
                            }
                            @if (manualSort()) {
                                <i
                                    class="sort-icon fas fa-list-ol"
                                    [title]="'Manual order' | translate"
                                ></i>
                            }
                        </button>
                        <button
                            [title]="'Measured at' | translate"
                            class="sortable right"
                            [class.sort-disabled]="manualSort()"
                            (click)="setSort('time')"
                        >
                            <i class="fas fa-clock"></i>
                            @if (sortCol() === 'time' && !manualSort()) {
                                <i
                                    [class]="
                                        'sort-icon fas fa-long-arrow-alt-up' +
                                        (sortDir() === 'desc'
                                            ? ' fa-flip-vertical'
                                            : '')
                                    "
                                ></i>
                            }
                        </button>
                    </div>
                    @for (item of warnings()!; track $index) {
                        <div
                            class="widget-list-row w-row"
                            [class.is-selected]="
                                widgetState.openVehicleInventoryNumber() ===
                                item.vehicle.inventoryNumber
                            "
                            [matTooltip]="
                                warningHelper.getShortHeader(
                                    item.warning.messageType
                                )
                            "
                            matTooltipClass="no-pointer-events"
                            (click)="
                                widgetState.openVehicle(
                                    item.vehicle.inventoryNumber
                                )
                            "
                            (keydown.enter)="
                                widgetState.openVehicle(
                                    item.vehicle.inventoryNumber
                                )
                            "
                        >
                            <span class="col-inv">{{
                                item.vehicle.inventoryNumberWithoutPrefix
                            }}</span>
                            @if (item.vehicle.routeNumber) {
                                <span class="col-route"
                                    ><span class="widget-tag">{{
                                        item.vehicle.routeNumber
                                    }}</span></span
                                >
                            } @else {
                                <span class="col-route"></span>
                            }
                            <span class="col-type">
                                <app-warning-icon
                                    [messageType]="item.warning.messageType"
                                ></app-warning-icon>
                                <span class="warning-name">{{
                                    warningHelper.getShortHeader(
                                        item.warning.messageType
                                    )
                                }}</span>
                            </span>
                            <span class="col-time">{{
                                item.warning.measuredAt | date: 'HH:mm'
                            }}</span>
                        </div>
                    }
                </div>
            }
        </div>
    }

    <div class="resize-handle" (mousedown)="bw.onDragStart($event)"></div>
</div>
